import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2, Download, Loader2, Copy, Globe, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { DashboardWidget } from "@/components/boards/DashboardWidget";
import { BOARD } from "@/constants/testIds";
import api from "@/lib/api";

export default function BoardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [explainingId, setExplainingId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [name, setName] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboards/${id}`);
      setDash(data.dashboard);
      setWidgets(data.widgets);
      setName(data.dashboard.name);
    } catch {
      toast.error("Could not load dashboard.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    if (!name.trim() || name === dash.name) return;
    const { data } = await api.patch(`/dashboards/${id}`, { name: name.trim() });
    setDash(data);
  };

  const explain = async (w) => {
    setExplainingId(w.id);
    try {
      const { data } = await api.post(`/dashboards/${id}/widgets/${w.id}/explain`);
      setWidgets((prev) => prev.map((x) => (x.id === w.id ? { ...x, explanation: data.explanation } : x)));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not explain this chart.");
    } finally {
      setExplainingId(null);
    }
  };

  const removeWidget = async (w) => {
    await api.delete(`/dashboards/${id}/widgets/${w.id}`);
    setWidgets((prev) => prev.filter((x) => x.id !== w.id));
  };

  const toggleShare = async (checked) => {
    const { data } = await api.patch(`/dashboards/${id}`, { share_mode: checked ? "public" : "private" });
    setDash(data);
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/dashboards/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(dash.name || "dashboard").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export PDF.");
    } finally {
      setPdfBusy(false);
    }
  };

  const publicUrl = dash?.share_token ? `${window.location.origin}/b/${dash.share_token}` : "";
  const embedCode = publicUrl
    ? `<iframe src="${publicUrl}" width="100%" height="720" frameborder="0" style="border:1px solid #e5e7eb;border-radius:12px"></iframe>`
    : "";
  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <WorkspaceNav />
        <div className="p-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></div>
      </div>
    );
  }
  if (!dash) return null;

  return (
    <div data-testid={BOARD.detailRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-6xl px-6 py-8">
        <button onClick={() => navigate("/boards")} className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> All dashboards
        </button>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <Input
            data-testid={BOARD.renameInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            className="h-auto max-w-md border-none bg-transparent px-0 text-2xl font-bold tracking-tight focus-visible:ring-0"
          />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid={BOARD.shareBtn} onClick={() => setShareOpen(true)} className="rounded-full text-xs">
              {dash.share_mode === "public" ? <Globe className="mr-1.5 h-3.5 w-3.5 text-accent" /> : <Share2 className="mr-1.5 h-3.5 w-3.5" />}
              Share
            </Button>
            <Button variant="outline" size="sm" data-testid={BOARD.pdfBtn} onClick={downloadPdf} disabled={pdfBusy} className="rounded-full text-xs">
              {pdfBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
              PDF
            </Button>
          </div>
        </div>

        {widgets.length ? (
          <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {widgets.map((w) => (
              <DashboardWidget key={w.id} widget={w} canEdit onExplain={explain} onRemove={removeWidget} explaining={explainingId === w.id} />
            ))}
          </div>
        ) : (
          <div data-testid={BOARD.detailEmpty} className="mt-10 rounded-xl border border-dashed border-border/60 p-12 text-center">
            <p className="text-sm text-foreground/90">This dashboard is empty.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Go to <Link to="/chat" className="text-accent underline">Ask</Link>, run a question, then click{" "}
              <b>Add to dashboard</b> on the answer.
            </p>
          </div>
        )}
      </main>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid={BOARD.shareDialog} className="font-mono sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share dashboard</DialogTitle>
            <DialogDescription>Make it public to share a link or embed it anywhere.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-lg border border-border/60 p-3">
              <span className="flex items-center gap-2 text-sm">
                {dash.share_mode === "public" ? <Globe className="h-4 w-4 text-accent" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                {dash.share_mode === "public" ? "Public — anyone with the link" : "Private — only your workspace"}
              </span>
              <Switch data-testid={BOARD.shareToggle} checked={dash.share_mode === "public"} onCheckedChange={toggleShare} />
            </div>

            {dash.share_mode === "public" && publicUrl && (
              <>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Public link</p>
                  <div className="flex gap-2">
                    <Input data-testid={BOARD.shareLink} readOnly value={publicUrl} className="text-xs" />
                    <Button size="sm" variant="outline" data-testid={BOARD.copyLink} onClick={() => copy(publicUrl, "Link")} className="shrink-0">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Embed</p>
                  <div className="flex gap-2">
                    <textarea data-testid={BOARD.shareEmbed} readOnly value={embedCode} rows={3} className="w-full rounded-md border border-border/60 bg-card/40 p-2 text-[11px] text-muted-foreground" />
                    <Button size="sm" variant="outline" data-testid={BOARD.copyEmbed} onClick={() => copy(embedCode, "Embed code")} className="shrink-0">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
